/**
 * NotesRepository — Onthouden notes, user-owned, server-confirmed writes.
 */
import { requireSupabase } from '@/lib/supabase/client'
import { noteFromRow, noteToInsert, noteToPatch, type NoteRow } from '@/lib/supabase/mappers'
import type { NotesRepository } from '../interfaces'
import { assertRowReturned, fail, requireUserId } from './helpers'

export const notesRepository: NotesRepository = {
  async list(includeArchived = false) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    let query = supabase
      .from('notes')
      .select('*')
      .eq('user_id', userId)
      .order('pinned', { ascending: false })
      .order('updated_at', { ascending: false })
    if (!includeArchived) query = query.eq('archived', false)
    const { data, error } = await query
    if (error) fail(error)
    return ((data ?? []) as NoteRow[]).map(noteFromRow)
  },

  async create(note) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('notes')
      .insert(noteToInsert(note, userId))
      .select()
      .single()
    return noteFromRow(assertRowReturned(data, error) as NoteRow)
  },

  async update(id, patch) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('notes')
      .update(noteToPatch(patch))
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single()
    return noteFromRow(assertRowReturned(data, error) as NoteRow)
  },

  async remove(id) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { error } = await supabase.from('notes').delete().eq('id', id).eq('user_id', userId)
    if (error) fail(error)
  },
}
